import { useMutation, useQueryClient } from '@tanstack/react-query';

import { toast } from '@ekhodealer/ekho-common/rev';

import type { TeamCreateMembersDto } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { teamEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to create (invite) team members
 * @param servicesProviderId - Required services provider ID
 * @param input - Team member creation input (emails + invitedBy)
 */
const createTeamMembers = async (
	servicesProviderId: string,
	input: TeamCreateMembersDto,
): Promise<void> => {
	await apiClient.post(teamEndpoints.members(servicesProviderId), input);
};

/**
 * Hook for creating (inviting) team members
 * Invalidates all team queries on success so the table refreshes
 */
export const useCreateTeamMembers = (): ReturnType<
	typeof useMutation<void, Error, TeamCreateMembersDto>
> => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: TeamCreateMembersDto) => {
			if (!servicesProviderId) {
				throw new Error('Cannot create team members without services provider ID');
			}
			return createTeamMembers(servicesProviderId, input);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queries.team._def,
			});
		},
		onError: (error) => {
			toast({
				message: `Failed to invite team members: ${(error as Error).message}`,
				variant: 'error',
			});
		},
	});
};
